/**
 * Iter 19.65.10 — KV-Kernregeln-Info-Modal
 *
 * Iter 19.65.11 — Konvertiert von rechts-seitigem Slide-Over zu zentriertem
 * Modal (User-Wunsch). Wird nach Klick auf das ⓘ-Icon neben dem gewählten
 * KV in der Wizard-Sidebar geöffnet. Lädt einmalig die KV-Details und zeigt
 * sie strukturiert an:
 *   - Header: KV-Name + Kürzel
 *   - Flag-Badges (Gleichwertigkeitsprüfung, Höchstanrechnung)
 *   - Kurzbeschreibung
 *   - Anrechnungs-Regeln (Vordienstzeiten)
 *   - Verwendungsgruppen + Tätigkeitsmerkmale + Berufstitel (aus `ruleset`)
 *   - Optional: Ausbildungs-Sonderregeln (`ausbildungsregeln`)
 *
 * Esc + Klick-außerhalb schließen.
 */
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Info, Scales, X } from "@phosphor-icons/react";

import { api, extractError } from "../lib/api";
import { getEquivalenceTerms } from "../pages/tool/shared";

function formatMarkdown(text) {
  // Sehr minimaler MD-Renderer: erkennt **bold**, ## headings, listen.
  // Wir splitten an Zeilen und rendern jede Zeile als <p>/<h3>/<li>.
  if (!text) return null;
  const lines = String(text).split(/\r?\n/);
  const blocks = [];
  let listBuf = [];

  const flushList = () => {
    if (listBuf.length) {
      blocks.push(
        <ul
          key={`ul-${blocks.length}`}
          className="list-disc pl-5 space-y-1 text-[13px] text-ink-800"
        >
          {listBuf.map((item, i) => (
            <li key={i}>{renderInline(item)}</li>
          ))}
        </ul>
      );
      listBuf = [];
    }
  };

  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i];
    const ln = raw.trim();
    if (!ln) { flushList(); continue; }
    const heading = ln.match(/^(#{1,3})\s+(.+)/);
    if (heading) {
      flushList();
      const lvl = heading[1].length;
      const cls =
        lvl === 1
          ? "font-cabinet text-base font-semibold text-ink-900 mt-3 mb-1"
          : lvl === 2
          ? "font-cabinet text-sm font-semibold text-ink-900 mt-2 mb-1"
          : "text-[13px] font-semibold text-ink-900 mt-2 mb-1";
      blocks.push(
        <div key={`h-${i}`} className={cls}>{renderInline(heading[2])}</div>
      );
      continue;
    }
    if (ln.startsWith("- ") || ln.startsWith("* ")) {
      listBuf.push(ln.slice(2));
      continue;
    }
    flushList();
    // Eigene Heuristik: Zeile in der Form „**Label:** Wert" wird als
    // dezenter Definitions-Pair gerendert.
    const boldPrefix = ln.match(/^\*\*([^*]+?)\*\*\s*:?\s*(.*)$/);
    if (boldPrefix) {
      blocks.push(
        <div key={`b-${i}`} className="text-[13px] text-ink-800 leading-snug">
          <span className="font-semibold text-ink-900">
            {boldPrefix[1]}
            {boldPrefix[2] ? ":" : ""}
          </span>{" "}
          {renderInline(boldPrefix[2])}
        </div>
      );
      continue;
    }
    blocks.push(
      <p key={`p-${i}`} className="text-[13px] text-ink-800 leading-snug">
        {renderInline(ln)}
      </p>
    );
  }
  flushList();
  return blocks;
}

function renderInline(text) {
  // Rendert **bold** inline. Mehr Inline-Syntax brauchen wir nicht.
  if (!text || !text.includes("**")) return text;
  const parts = text.split(/(\*\*[^*]+?\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith("**") && p.endsWith("**") ? (
      <strong key={i} className="font-semibold text-ink-900">
        {p.slice(2, -2)}
      </strong>
    ) : (
      <React.Fragment key={i}>{p}</React.Fragment>
    )
  );
}

/**
 * Gruppiert die rohe gehaltsstufen_tabelle nach `gruppe`, damit das Sheet
 * pro Verwendungsgruppe einen Block mit allen Stufen zeigt.
 */
function groupGehaltsstufen(rows) {
  const map = new Map();
  for (const r of rows || []) {
    const g = (r.gruppe || "").trim() || "—";
    if (!map.has(g)) map.set(g, []);
    map.get(g).push(r);
  }
  return Array.from(map.entries()).map(([gruppe, stufen]) => ({ gruppe, stufen }));
}

export default function KvDetailsSheet({ kvId, kvName, kvKuerzel, open, onClose }) {
  const closeBtnRef = useRef(null);
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Esc + body-scroll-lock + focus-management — gleiche Konvention wie
  // HaftungsausschlussModal.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    setTimeout(() => closeBtnRef.current?.focus(), 50);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  // Lade Details bei Öffnen oder kvId-Wechsel.
  useEffect(() => {
    if (!open || !kvId) return;
    let aborted = false;
    setLoading(true);
    setError("");
    setDetails(null);
    api
      .get(`/tool/kvs/${kvId}/details`)
      .then(({ data }) => { if (!aborted) setDetails(data); })
      .catch((err) => {
        if (!aborted) setError(extractError(err, "Details konnten nicht geladen werden."));
      })
      .finally(() => { if (!aborted) setLoading(false); });
    return () => { aborted = true; };
  }, [open, kvId]);

  if (!open) return null;

  // Iter 19.65.12 — Render via React-Portal direkt nach `document.body`,
  // damit das Modal NICHT im Stacking-Context des Sidebars
  // (`<aside class="sticky top-0">` in UserLayout) gefangen ist. Ohne
  // Portal wird `fixed inset-0` durch den Sticky-Parent eingeschränkt
  // und das Modal erscheint nur im Sidebar-Bereich statt viewport-weit.
  return createPortal(
    <div
      data-testid="kv-details-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="kv-details-modal-title"
      className="fixed inset-0 z-[60] flex items-center justify-center p-3 sm:p-6 bg-ink-900/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-2xl max-h-[90vh] bg-white border border-ink-200 rounded-sm shadow-xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-6 sm:px-7 py-5 border-b border-ink-200 shrink-0">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-500 mb-1 flex items-center gap-2">
              <Info size={12} weight="fill" />
              Kernregeln des Kollektivvertrags
            </div>
            <h2
              id="kv-details-modal-title"
              className="font-cabinet text-xl sm:text-2xl text-ink-900 tracking-tight font-medium truncate"
              title={kvName || ""}
            >
              {kvName || "Details"}
            </h2>
            {kvKuerzel && (
              <div className="font-cabinet text-[11px] font-black tracking-widest uppercase text-warm-500 mt-1">
                {kvKuerzel}
              </div>
            )}
          </div>
          <button
            ref={closeBtnRef}
            type="button"
            onClick={onClose}
            aria-label="Schließen"
            className="p-2 -mr-2 -mt-1 rounded-sm hover:bg-warm-100 text-warm-600 hover:text-ink-900 transition-crisp"
            data-testid="kv-details-modal-close"
          >
            <X size={18} weight="bold" />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto px-6 sm:px-7 py-5 space-y-6">
          {loading && (
            <div className="flex flex-col items-center justify-center py-12 text-warm-500">
              <div className="animate-pulse text-[13px]">Lade Kernregeln …</div>
            </div>
          )}
          {error && !loading && (
            <div className="rounded-sm border border-red-300 bg-red-50 px-3 py-2 text-[13px] text-red-700">
              {error}
            </div>
          )}
          {details && !loading && (
            <>
              {/* Flag-Badges */}
              <div className="flex flex-wrap gap-2">
                {details.equivalence_check_required && (() => {
                  const terms = getEquivalenceTerms(details.equivalence_terminology);
                  return (
                    <span
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-sm text-[11px] font-medium uppercase tracking-wider border border-brand-400 bg-brand-50 text-brand-700"
                      title={`${terms.pruefung} erforderlich`}
                    >
                      <Scales size={12} weight="bold" />
                      {terms.pruefung}
                    </span>
                  );
                })()}
                {details.cap_months != null && details.cap_months > 0 && (
                  <span className="inline-flex items-center px-2 py-1 rounded-sm text-[11px] font-medium uppercase tracking-wider border border-ink-300 bg-warm-50 text-ink-800">
                    Höchstanrechnung {Math.round(details.cap_months / 12)}{" "}
                    {Math.round(details.cap_months / 12) === 1 ? "Jahr" : "Jahre"}
                  </span>
                )}
              </div>

              {/* Kurzbeschreibung */}
              {details.short_description && (
                <section data-testid="kv-details-short-description">
                  <h3 className="text-[10px] uppercase tracking-widest font-semibold text-warm-500 mb-2">
                    Kurzbeschreibung
                  </h3>
                  <p className="text-[13px] text-ink-800 leading-relaxed">
                    {details.short_description}
                  </p>
                </section>
              )}

              {/* Anrechnungs-Regeln (Vordienstzeiten) */}
              {details.vordienst_anrechnungsregeln && (
                <section data-testid="kv-details-vordienst">
                  <h3 className="text-[10px] uppercase tracking-widest font-semibold text-warm-500 mb-2">
                    Anrechnung von Vordienstzeiten
                  </h3>
                  <div className="space-y-2">
                    {formatMarkdown(details.vordienst_anrechnungsregeln)}
                  </div>
                </section>
              )}

              {/* Verwendungsgruppen / Ruleset */}
              {details.ruleset && (
                <section data-testid="kv-details-ruleset">
                  <h3 className="text-[10px] uppercase tracking-widest font-semibold text-warm-500 mb-2">
                    Verwendungsgruppen &amp; Tätigkeitsmerkmale
                  </h3>
                  <div className="space-y-2">
                    {formatMarkdown(details.ruleset)}
                  </div>
                </section>
              )}

              {/* Ausbildungs-Sonderregeln */}
              {details.ausbildungsregeln && details.ausbildungsregeln.length > 0 && (
                <section data-testid="kv-details-ausbildung">
                  <h3 className="text-[10px] uppercase tracking-widest font-semibold text-warm-500 mb-2">
                    Ausbildungs-Sonderregeln
                  </h3>
                  <ul className="space-y-1.5">
                    {details.ausbildungsregeln.map((a, i) => (
                      <li key={i} className="text-[13px] text-ink-800 leading-snug">
                        <span className="font-semibold text-ink-900">{a.label}:</span>{" "}
                        {a.months} Monate anrechenbar
                        {a.bypass_cap && (
                          <span className="ml-1 text-[11px] uppercase tracking-wider text-brand-700">
                            (übersteigt Höchstanrechnung)
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {/* Gehaltsstufen-Tabelle (kompakte Vorschau) */}
              {details.gehaltsstufen_tabelle && details.gehaltsstufen_tabelle.length > 0 && (
                <section data-testid="kv-details-gehaltsstufen">
                  <h3 className="text-[10px] uppercase tracking-widest font-semibold text-warm-500 mb-2">
                    Gehalts-Stufen (Übersicht)
                  </h3>
                  <div className="space-y-3">
                    {groupGehaltsstufen(details.gehaltsstufen_tabelle).map((g) => (
                      <div key={g.gruppe} className="border border-ink-200 rounded-sm overflow-hidden">
                        <div className="bg-warm-50 px-3 py-1.5 text-[12px] font-semibold text-ink-900 border-b border-ink-200">
                          Gruppe {g.gruppe}
                        </div>
                        <ul className="divide-y divide-ink-100">
                          {g.stufen.map((s, i) => (
                            <li
                              key={i}
                              className="px-3 py-1.5 text-[12px] text-ink-700 flex items-center justify-between gap-2"
                            >
                              <span>{s.jahre || "—"}</span>
                              {s.stufe && (
                                <span className="text-warm-500 text-[11px] uppercase tracking-wider">
                                  Stufe {s.stufe}
                                </span>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
